import React from "react";
import { OptInCustomer } from "../../api/optInOffers";
import { formatCurrency, formatInteger } from "../../helpers/formatNumber";
import { formatCopenhagenDateTime } from "../../helpers/dateFilters";

const BADGE = "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium";

const StatusBadges: React.FC<{ customer: OptInCustomer }> = ({ customer }) => (
  <div className="flex flex-wrap gap-1">
    {customer.optInStatus === "OptedIn" && (
      <span className={`${BADGE} bg-green-100 text-green-800`}>Opted in</span>
    )}
    {customer.optInStatus === "OptedOut" && (
      <span className={`${BADGE} bg-gray-100 text-gray-800`}>Opted out</span>
    )}
    {customer.missedCount > 0 && (
      <span className={`${BADGE} bg-red-100 text-red-800`}>
        Missed {formatInteger(customer.missedCount)}
      </span>
    )}
    {customer.isDeleted && (
      <span className={`${BADGE} bg-gray-100 text-gray-800`}>Deleted account</span>
    )}
  </div>
);

// When the person entered their current opt-in state.
const since = (customer: OptInCustomer): string | null => {
  if (customer.optInStatus === "OptedIn") return customer.optedInAt;
  if (customer.optInStatus === "OptedOut") return customer.optedOutAt;
  return null;
};

const CountAmount: React.FC<{ count: number; amount: number; className?: string }> = ({
  count,
  amount,
  className,
}) =>
  count === 0 ? (
    <>0</>
  ) : (
    <span className={className}>
      {formatInteger(count)} · {formatCurrency(amount)} kr
    </span>
  );

interface OptInCustomersTableProps {
  customers: OptInCustomer[];
  onSelectPerson: (identityId: string) => void;
  emptyMessage: string;
  loading: boolean;
}

export const OptInCustomersTable: React.FC<OptInCustomersTableProps> = ({
  customers,
  onSelectPerson,
  emptyMessage,
  loading,
}) => (
  <div className="overflow-x-auto">
    <table className="table-default">
      <thead>
        <tr>
          <th>Name</th>
          <th>E-mail</th>
          <th>Status</th>
          <th>Since</th>
          <th>Paid</th>
          <th>Missed</th>
          <th>Compensated</th>
          <th>Not compensated</th>
        </tr>
      </thead>
      <tbody>
        {customers.length === 0 && !loading ? (
          <tr>
            <td colSpan={8} className="text-center text-gray-500">
              {emptyMessage}
            </td>
          </tr>
        ) : (
          customers.map((customer) => {
            const sinceAt = since(customer);
            return (
              <tr key={customer.identityId}>
                <td className="whitespace-nowrap">
                  <button
                    type="button"
                    className="underline text-left"
                    onClick={() => onSelectPerson(customer.identityId)}
                  >
                    {customer.name ?? "—"}
                  </button>
                </td>
                <td className="whitespace-nowrap">{customer.email ?? "—"}</td>
                <td>
                  <StatusBadges customer={customer} />
                </td>
                <td className="whitespace-nowrap">
                  {sinceAt ? formatCopenhagenDateTime(sinceAt) : "—"}
                </td>
                <td className="whitespace-nowrap">
                  <CountAmount count={customer.paidCount} amount={customer.paidCashback} />
                </td>
                <td className="whitespace-nowrap">
                  <CountAmount
                    count={customer.missedCount}
                    amount={customer.missedAmount}
                    className="text-red-600"
                  />
                </td>
                <td className="whitespace-nowrap">
                  <CountAmount
                    count={customer.compensatedCount}
                    amount={customer.compensatedCashback}
                  />
                </td>
                <td className="whitespace-nowrap">
                  {customer.notCompensatedCount > 0 ? (
                    <span className="text-red-600">
                      {formatInteger(customer.notCompensatedCount)}
                    </span>
                  ) : (
                    "0"
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  </div>
);
